import { useMemo } from "react";
import { Plus, Trash, Broom, ShieldCheck, Flask, Package } from "@phosphor-icons/react";
import { formatCurrency, formatCurrencyExact } from "../../lib/romEngine";
import { computeCleaning } from "../../lib/tmEngine";
import { Section, Field } from "./primitives";

const SURFACE_TASKS = [
    { key: "hepa_floor_ceiling", label: "HEPA vacuum — detailed (floor + ceiling)" },
    { key: "hepa_walls", label: "HEPA vacuum — light (walls)" },
    { key: "clean_floor", label: "Clean floor" },
    { key: "clean_walls", label: "Clean walls" },
    { key: "clean_ceiling", label: "Clean ceiling" },
];

function byKind(schedule, kind) {
    return (schedule?.schedule_f || []).filter((r) => r.kind === kind);
}

/* ---------------- Cleaning input panel ---------------- */
export function CleaningPanel({ cleaning, schedule, onChange }) {
    const c = cleaning;
    const set = (patch) => onChange(patch);
    const setTask = (k, v) => set({ tasks: { ...(c.tasks || {}), [k]: v } });

    const openingOpts = byKind(schedule, "unit");
    const labourOpts = byKind(schedule, "labour");
    const equipOpts = byKind(schedule, "equipment");
    const rateOf = (item) => Number((schedule?.schedule_f || []).find((x) => x.item === item)?.rate || 0);

    const addOpening = () => set({ openings: [...(c.openings || []), { item: openingOpts[0]?.item || "", qty: 1 }] });
    const updOpening = (i, k, v) => set({ openings: (c.openings || []).map((o, idx) => idx === i ? { ...o, [k]: v } : o) });
    const delOpening = (i) => set({ openings: (c.openings || []).filter((_, idx) => idx !== i) });

    const addLabour = () => set({ labour: [...(c.labour || []), { role: labourOpts[0]?.item || "", qty: 1, hours: 8 }] });
    const updLabour = (i, k, v) => set({ labour: (c.labour || []).map((o, idx) => idx === i ? { ...o, [k]: v } : o) });
    const delLabour = (i) => set({ labour: (c.labour || []).filter((_, idx) => idx !== i) });

    const addEquip = () => set({ equipment: [...(c.equipment || []), { item: equipOpts[0]?.item || "", qty: 1, days: 1 }] });
    const updEquip = (i, k, v) => set({ equipment: (c.equipment || []).map((o, idx) => idx === i ? { ...o, [k]: v } : o) });
    const delEquip = (i) => set({ equipment: (c.equipment || []).filter((_, idx) => idx !== i) });
    const isDaily = (item) => !!(schedule?.schedule_f || []).find((x) => x.item === item)?.daily;

    return (
        <div className="space-y-6" data-testid="cleaning-panel">
            <Section tag="A" title="Surfaces to clean">
                <p className="text-xs text-[var(--rom-text-2)] mb-3">Enter each surface separately — floor, wall and ceiling areas usually differ. Surface tasks below are priced per sq ft against these.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Field label="Floor sq ft">
                        <input type="number" min={0} className="input font-mono" placeholder="e.g. 14101"
                            value={c.floorSqft} onChange={(e) => set({ floorSqft: e.target.value })} data-testid="cleaning-floor-sqft" />
                    </Field>
                    <Field label="Wall sq ft">
                        <input type="number" min={0} className="input font-mono" placeholder="e.g. 26945"
                            value={c.wallSqft} onChange={(e) => set({ wallSqft: e.target.value })} data-testid="cleaning-wall-sqft" />
                    </Field>
                    <Field label="Ceiling sq ft">
                        <input type="number" min={0} className="input font-mono" placeholder="e.g. 14101"
                            value={c.ceilingSqft} onChange={(e) => set({ ceilingSqft: e.target.value })} data-testid="cleaning-ceiling-sqft" />
                    </Field>
                </div>
            </Section>

            <Section tag="B" title="Cleaning tasks">
                <div className="space-y-2">
                    {SURFACE_TASKS.map((t) => (
                        <label key={t.key} className="flex items-center gap-2.5 text-sm cursor-pointer py-1" data-testid={`cleaning-task-${t.key}`}>
                            <input type="checkbox" className="accent-[var(--rom-orange)] w-4 h-4"
                                checked={!!c.tasks?.[t.key]} onChange={(e) => setTask(t.key, e.target.checked)} />
                            <Broom size={15} weight="bold" className="text-[var(--rom-text-3)]" />
                            <span>{t.label}</span>
                        </label>
                    ))}
                    <div className="border-t border-[var(--rom-border)] my-2" />
                    <label className="flex items-center gap-2.5 text-sm cursor-pointer py-1" data-testid="cleaning-antimicrobial">
                        <input type="checkbox" className="accent-[var(--rom-orange)] w-4 h-4"
                            checked={!!c.antimicrobial} onChange={(e) => set({ antimicrobial: e.target.checked })} />
                        <ShieldCheck size={15} weight="bold" className="text-[var(--rom-green)]" />
                        <span>Apply anti-microbial agent <span className="text-[var(--rom-text-3)]">(all surfaces)</span></span>
                    </label>
                    <div className="flex items-center gap-2.5 text-sm py-1">
                        <label className="flex items-center gap-2.5 cursor-pointer" data-testid="cleaning-atp">
                            <input type="checkbox" className="accent-[var(--rom-orange)] w-4 h-4"
                                checked={!!c.atp} onChange={(e) => set({ atp: e.target.checked })} />
                            <Flask size={15} weight="bold" className="text-[var(--rom-navy)]" />
                            <span>On-site ATP verification testing</span>
                        </label>
                        {c.atp && (
                            <input type="number" min={1} className="input font-mono w-20 ml-1 py-1 px-2" value={c.atpQty}
                                onChange={(e) => set({ atpQty: e.target.value })} data-testid="cleaning-atp-qty" title="Number of ATP tests" />
                        )}
                    </div>
                </div>
            </Section>

            <Section tag="C" title="Openings & add-ons">
                <p className="text-xs text-[var(--rom-text-2)] mb-3">Doors, windows and debris hauls — priced per each.</p>
                <div className="space-y-2">
                    {(c.openings || []).map((o, i) => (
                        <div key={i} className="flex items-center gap-2" data-testid={`cleaning-opening-${i}`}>
                            <select className="select flex-1" value={o.item} onChange={(e) => updOpening(i, "item", e.target.value)}>
                                {openingOpts.map((x) => <option key={x.item} value={x.item}>{x.item} (${x.rate.toFixed(2)})</option>)}
                            </select>
                            <input type="number" min={0} className="input font-mono w-24 py-1.5 px-2" value={o.qty} onChange={(e) => updOpening(i, "qty", e.target.value)} title="Qty" />
                            <span className="font-mono text-sm tabular w-24 text-right">{formatCurrencyExact((Number(o.qty) || 0) * rateOf(o.item))}</span>
                            <button onClick={() => delOpening(i)} className="text-[var(--rom-danger)] hover:opacity-70 shrink-0"><Trash size={15} /></button>
                        </div>
                    ))}
                </div>
                {openingOpts.length > 0 && (
                    <button onClick={addOpening} className="btn btn-ghost btn-sm mt-2" data-testid="cleaning-add-opening"><Plus size={12} weight="bold" /> Add opening / haul</button>
                )}
            </Section>

            <Section tag="D" title="Labour (optional)">
                <div className="space-y-2">
                    {(c.labour || []).map((l, i) => (
                        <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`cleaning-labour-${i}`}>
                            <select className="select flex-1 min-w-[180px]" value={l.role} onChange={(e) => updLabour(i, "role", e.target.value)}>
                                {labourOpts.map((x) => <option key={x.item} value={x.item}>{x.item} (${x.rate.toFixed(2)}/hr)</option>)}
                            </select>
                            <input type="number" min={0} className="input font-mono w-20 py-1.5 px-2" value={l.qty} onChange={(e) => updLabour(i, "qty", e.target.value)} title="People" placeholder="ppl" />
                            <span className="text-xs text-[var(--rom-text-3)]">×</span>
                            <input type="number" min={0} className="input font-mono w-20 py-1.5 px-2" value={l.hours} onChange={(e) => updLabour(i, "hours", e.target.value)} title="Hours" placeholder="hrs" />
                            <span className="font-mono text-sm tabular w-24 text-right">{formatCurrencyExact((Number(l.qty) || 0) * (Number(l.hours) || 0) * rateOf(l.role))}</span>
                            <button onClick={() => delLabour(i)} className="text-[var(--rom-danger)] hover:opacity-70 shrink-0"><Trash size={15} /></button>
                        </div>
                    ))}
                </div>
                {labourOpts.length > 0 && (
                    <button onClick={addLabour} className="btn btn-ghost btn-sm mt-2" data-testid="cleaning-add-labour"><Plus size={12} weight="bold" /> Add labour line</button>
                )}
            </Section>

            <Section tag="E" title="Equipment & consumables (optional)">
                <div className="space-y-2">
                    {(c.equipment || []).map((e, i) => (
                        <div key={i} className="flex items-center gap-2 flex-wrap" data-testid={`cleaning-equipment-${i}`}>
                            <select className="select flex-1 min-w-[180px]" value={e.item} onChange={(ev) => updEquip(i, "item", ev.target.value)}>
                                {equipOpts.map((x) => <option key={x.item} value={x.item}>{x.item} (${x.rate.toFixed(2)})</option>)}
                            </select>
                            <input type="number" min={0} className="input font-mono w-20 py-1.5 px-2" value={e.qty} onChange={(ev) => updEquip(i, "qty", ev.target.value)} title="Qty" placeholder="qty" />
                            {isDaily(e.item) && (
                                <>
                                    <span className="text-xs text-[var(--rom-text-3)]">×</span>
                                    <input type="number" min={0} className="input font-mono w-20 py-1.5 px-2" value={e.days} onChange={(ev) => updEquip(i, "days", ev.target.value)} title="Days" placeholder="days" />
                                </>
                            )}
                            <span className="font-mono text-sm tabular w-24 text-right">{formatCurrencyExact((Number(e.qty) || 0) * (isDaily(e.item) ? (Number(e.days) || 1) : 1) * rateOf(e.item))}</span>
                            <button onClick={() => delEquip(i)} className="text-[var(--rom-danger)] hover:opacity-70 shrink-0"><Trash size={15} /></button>
                        </div>
                    ))}
                </div>
                {equipOpts.length > 0 && (
                    <button onClick={addEquip} className="btn btn-ghost btn-sm mt-2" data-testid="cleaning-add-equipment"><Plus size={12} weight="bold" /> Add equipment line</button>
                )}
                <div className="border-t border-[var(--rom-border)] mt-4 pt-4">
                    <label className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid="cleaning-ppe-toggle">
                        <input type="checkbox" className="accent-[var(--rom-orange)] w-4 h-4" checked={!!c.includePPE} onChange={(ev) => set({ includePPE: ev.target.checked })} />
                        <Package size={15} weight="bold" className="text-[var(--rom-text-3)]" />
                        <span>Add PPE consumables (scales by tech × days)</span>
                    </label>
                    {c.includePPE && (
                        <div className="grid grid-cols-2 gap-3 mt-3 max-w-sm">
                            <Field label="Tech count"><input type="number" min={0} className="input font-mono" value={c.techCount} onChange={(ev) => set({ techCount: ev.target.value })} data-testid="cleaning-ppe-techcount" /></Field>
                            <Field label="Days on site"><input type="number" min={0} className="input font-mono" value={c.days} onChange={(ev) => set({ days: ev.target.value })} data-testid="cleaning-ppe-days" /></Field>
                        </div>
                    )}
                </div>
            </Section>
        </div>
    );
}

/* ---------------- Cleaning total card (sticky right) ---------------- */
export function CleaningTotalCard({ result }) {
    const total = result.grand_total;
    const groups = useMemo(() => Object.entries(result.groups || {}).sort((a, b) => b[1] - a[1]), [result.groups]);
    return (
        <div className="rom-card border-t-4 border-t-[var(--rom-orange)]" data-testid="cleaning-total-card">
            <div className="px-5 pt-5">
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-2)] font-semibold">Cleaning quote total</div>
                <div className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-[var(--rom-navy)] mt-1" data-testid="cleaning-grand-total">
                    {formatCurrency(total)}
                </div>
                <div className="text-[11px] text-[var(--rom-text-3)] mt-1">Standalone commercial cleaning · flat unit-rate pricing</div>
            </div>
            <div className="px-5 pt-4 pb-5">
                {groups.length === 0 ? (
                    <div className="p-3 rounded-[4px] bg-slate-50 border border-dashed border-[var(--rom-border-strong)] text-xs text-[var(--rom-text-2)]">
                        Enter surface sq ft and pick cleaning tasks to build the quote.
                    </div>
                ) : (
                    <div className="space-y-2">
                        {groups.map(([g, v]) => (
                            <div key={g} className="flex items-center justify-between text-sm" data-testid={`cleaning-group-${g}`}>
                                <span className="text-[var(--rom-text-2)]">{g}</span>
                                <span className="font-mono font-semibold tabular">{formatCurrencyExact(v)}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {result.lines?.length > 0 && (
                <div className="border-t border-[var(--rom-border)] px-5 py-3 max-h-64 overflow-y-auto">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--rom-text-3)] font-bold mb-2">Line items</div>
                    {result.lines.map((l, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 text-xs py-1" data-testid={`cleaning-line-${i}`}>
                            <span className="text-[var(--rom-text-2)] truncate">{l.item} <span className="text-[var(--rom-text-3)]">· {Number(l.qty).toLocaleString()} {l.unit}</span></span>
                            <span className="font-mono tabular shrink-0">{formatCurrencyExact(l.subtotal)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
